import type { PaginatedResponse } from '@packages/types';

/**
 * The shared walk over every shape a React Query list cache can hold for a
 * row-keyed resource: an infinite-query cache (`{ pages, pageParams }`), a
 * single-page `PaginatedResponse<T>`, or (removal only) a flat array.
 * `applyRowUpdateToCache` and `useOptimisticListMutation` build on these.
 */

function isPaginatedResponse<T>(value: unknown): value is PaginatedResponse<T> {
  return typeof value === 'object' && value !== null && 'data' in value && Array.isArray(value.data);
}

function isInfiniteShape<T>(value: unknown): value is { pages: PaginatedResponse<T>[] } & Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'pages' in value &&
    Array.isArray(value.pages) &&
    value.pages.every((page) => isPaginatedResponse<T>(page))
  );
}

/**
 * Maps every row of an infinite or single-page cache through `fn`. A row `fn`
 * returns unchanged keeps its identity, and a page or cache whose rows are all
 * unchanged is still rebuilt, so callers wanting stable references must make
 * `fn` return the same row object when nothing changed.
 */
export function mapRowsInCache<T>(old: unknown, fn: (row: T) => T): unknown {
  if (isInfiniteShape<T>(old)) {
    return { ...old, pages: old.pages.map((page) => ({ ...page, data: page.data.map(fn) })) };
  }
  if (isPaginatedResponse<T>(old)) {
    return { ...old, data: old.data.map(fn) };
  }
  return old;
}

/** Removes every row matching `predicate` from a flat, infinite or single-page cache. */
export function removeRowsFromCache<T>(old: unknown, predicate: (row: T) => boolean): unknown {
  if (old == null) return old;
  if (Array.isArray(old)) return (old as T[]).filter((row) => !predicate(row));
  if (isInfiniteShape<T>(old)) {
    return { ...old, pages: old.pages.map((page) => ({ ...page, data: page.data.filter((row) => !predicate(row)) })) };
  }
  if (isPaginatedResponse<T>(old)) {
    return { ...old, data: old.data.filter((row) => !predicate(row)) };
  }
  return old;
}
